import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styles from "./MOSFragebogen.module.css";
import FootDiagram from "./FootDiagram";
import { useMOSFragebogen, defaultT1, defaultPP } from "../../context/MOSFragebogenContext";

const API = process.env.REACT_APP_API_URL;

function authHeaders() {
  try {
    const stored = JSON.parse(localStorage.getItem("AUTH_DATA") || "{}");
    return { "Content-Type": "application/json", Authorization: `Bearer ${stored.jwt}` };
  } catch {
    return { "Content-Type": "application/json" };
  }
}

// ── Defaults for null fields ───────────────────────────────────────────────
// Every field must be defined (empty string, not undefined): an undefined value turns
// a text/number input uncontrolled, and it then keeps showing the previous episode's text.
const DEFAULT_T1 = defaultT1;
const DEFAULT_PP = defaultPP;

// ── Shared data ────────────────────────────────────────────────────────────
export const AKTIVITAETEN = [
  "Im Haus gehen", "Im Garten gehen", "Hausarbeiten erledigen",
  "Lebensmittel einkaufen", "Berufliche Tätigkeiten ausüben",
  "Hobbys nachgehen", "In der Stadt einkaufen gehen",
  "Spazieren gehen", "Sport treiben",
];
const BERUFSFELDER = [
  "Gesundheits-, Pflege- und Sozialberufe",
  "Erziehungs-, Bildungs- und Schulberufe",
  "Technische Berufe (z. B. Ingenieurwesen, Maschinenbau)",
  "IT- und Informatikberufe", "Naturwissenschaftliche Berufe",
  "Kaufmännische und Verwaltungsberufe", "Bau- und Handwerksberufe",
  "Kunst-, Medien-, Designberufe", "Sicherheits- und Schutzberufe",
  "Dienstleistungsberufe (z. B. Gastronomie, Reinigung)",
  "Verkehr und Logistik", "Land- und Forstwirtschaft",
  "Andere / keine berufliche Tätigkeit zurzeit",
];
const BERUFSSTATUS = [
  "Vollzeit erwerbstätig", "Teilzeit erwerbstätig",
  "Selbstständig / freiberuflich", "In Ausbildung / Studium",
  "Arbeitssuchend", "In Elternzeit / Pflegezeit",
  "Rentner / Pensionär", "Krankgeschrieben / in Rehabilitation", "Sonstiges",
];
const ALTER = [
  "18–29 Jahre", "30–39 Jahre", "40–49 Jahre", "50–59 Jahre",
  "60–69 Jahre", "70–79 Jahre", "80–89 Jahre", "90–99 Jahre",
];
const GEHSTRECKE_NOW = [
  "Ich kann mich nur in meiner Wohnung bewegen (0–10 Meter)",
  "Ich kann zum Nachbarn gehen (10–50 Meter)",
  "Ich kann bis zur Straßenecke gehen (50–200 Meter)",
  "Ich kann zu Geschäften in der Nachbarschaft gehen (200 Meter – 1 Kilometer)",
  "Ich kann längere Strecken ohne Pause gehen (mehr als 1 Kilometer)",
];
const GEHSTRECKE_EXP = [
  "...nur in der Wohnung (0–10 Meter)", "...zum Nachbarn (10–50 Meter)",
  "...zur Straßenecke (50–200 Meter)",
  "...zu Geschäften in der Nachbarschaft (200 Meter – 1 Kilometer)",
  "...längere Strecken ohne Pause (mehr als 1 Kilometer)",
];

// ── Reusable sub-components ────────────────────────────────────────────────
function Scale({ n, label, hint, left, right, name, value, onChange }) {
  return (
    <div className={styles.question}>
      <p className={styles.questionLabel}>
        {n && <span className={styles.qNum}>{n}.</span>} {label}
        {hint && <span className={styles.hint}> {hint}</span>}
      </p>
      <div className={styles.scaleRow}>
        <span className={styles.scaleEndLabel}>{left}</span>
        <div className={styles.scaleOptions}>
          {[1,2,3,4,5,6,7,8,9,10].map((p) => (
            <label key={p} className={styles.scalePt}>
              <input
                type="radio" name={name} value={String(p)}
                checked={value === String(p)} onChange={() => onChange(String(p))}
                className={styles.hiddenRadio}
              />
              <span className={`${styles.scaleCircle}${value === String(p) ? " " + styles.scaleCircleActive : ""}`}>{p}</span>
            </label>
          ))}
        </div>
        <span className={styles.scaleEndLabel}>{right}</span>
      </div>
    </div>
  );
}

function Radios({ n, label, hint, name, opts, value, onChange }) {
  return (
    <div className={styles.question}>
      <p className={styles.questionLabel}>
        {n && <span className={styles.qNum}>{n}.</span>} {label}
        {hint && <span className={styles.hint}> {hint}</span>}
      </p>
      <div className={styles.optionList}>
        {opts.map((opt) => {
          const v = typeof opt === "string" ? opt : opt.value;
          const l = typeof opt === "string" ? opt : opt.label;
          return (
            <label key={v} className={styles.option}>
              <input type="radio" name={name} value={v} checked={value === v} onChange={() => onChange(v)} />
              <span>{l}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
}

function Matrix({ n, label, hint, note, prefix, state, onChange }) {
  return (
    <div className={styles.question}>
      <p className={styles.questionLabel}>
        {n && <span className={styles.qNum}>{n}.</span>} {label}
        {hint && <span className={styles.hint}> {hint}</span>}
      </p>
      {note && <p className={styles.matrixNote}>{note}</p>}
      <div className={styles.matrixTable}>
        <div className={styles.matrixHeader}>
          <span />
          <span>Weniger</span>
          <span>Mehr</span>
          <span>Keine Veränderung</span>
        </div>
        {AKTIVITAETEN.map((act) => (
          <div key={act} className={styles.matrixRow}>
            <span className={styles.matrixActivity}>{act}</span>
            {["weniger","mehr","keine"].map((c) => (
              <label key={c} className={styles.matrixCell}>
                <input type="radio" name={`${prefix}_${act}`} value={c}
                  checked={state[act] === c} onChange={() => onChange(act, c)} />
              </label>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function Txt({ n, label, hint, id, value, onChange, rows = 3, maxLength }) {
  return (
    <div className={styles.question}>
      <label className={styles.questionLabel} htmlFor={id}>
        {n && <span className={styles.qNum}>{n}.</span>} {label}
        {hint && <span className={styles.hint}> {hint}</span>}
      </label>
      <textarea id={id} className={styles.textarea} value={value ?? ""}
        onChange={(e) => onChange(e.target.value)} rows={rows}
        maxLength={maxLength} />
      {maxLength && (
        <span className={styles.charCount}>{(value || "").length}/{maxLength}</span>
      )}
    </div>
  );
}

function SkipNote({ text }) {
  return <p className={styles.skipNote}>→ {text}</p>;
}

const fmtDate = (d) => d ? new Date(d).toLocaleDateString("de-DE") : "–";

// ── Main component ─────────────────────────────────────────────────────────
export default function MOSFragebogen({ token = null, code = null, readOnly: readOnlyProp = false, viewData: viewDataProp = null, fragebogenId: fragebogenIdProp = null, onDone = null, startAtEK = false, prefillName = null, prefillVorname = null }) {
  const navigate  = useNavigate();
  const location  = useLocation();
  const navState  = location.state || {};
  const resolvedFragebogenId = fragebogenIdProp || viewDataProp?.id || (navState.readOnly ? navState.fragebogenId : null);

  // readOnly mode: either passed as prop (public page) or via navigation state (admin list)
  const readOnly = readOnlyProp || navState.readOnly === true;

  const { data, setData, setT1, setT2, setPP, clearMOSFragebogen } = useMOSFragebogen();
  const [saving, setSaving]           = useState(false);
  const [saveMsg, setSaveMsg]         = useState("");
  const [viewData, setViewData]       = useState(viewDataProp);
  const [loadingRec, setLoadingRec]   = useState(false);
  const [localPP, setLocalPP]         = useState({ ...DEFAULT_PP });
  const [showSuccess, setShowSuccess]     = useState(false);
  const [showEKSuccess, setShowEKSuccess] = useState(false);
  const [ekJustSaved, setEKJustSaved]     = useState(false);
  // Keep fragebogenId in local state so it survives context clear after VE save
  const [savedFragebogenId, setSavedFragebogenId] = useState(null);
  // Episode selector: undefined = not yet decided, null = new EK, object = viewing existing episode
  const [selectedEpisode, setSelectedEpisode] = useState(undefined);

  // Public form: a name stored on the MOS-Link is fixed and cannot be changed by the patient
  const nameLocked    = !!token && !!prefillName;
  const vornameLocked = !!token && !!prefillVorname;

  useEffect(() => {
    if (!readOnly && (prefillName || prefillVorname)) {
      setData((prev) => ({
        ...prev,
        name:    prefillName    || prev.name    || '',
        vorname: prefillVorname || prev.vorname || '',
      }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch the record when in readOnly mode with a fragebogenId
  useEffect(() => {
    if (readOnly && resolvedFragebogenId && !viewData) {
      setLoadingRec(true);
      fetch(`${API}/api/mos-fragebogen/${resolvedFragebogenId}`, { headers: authHeaders() })
        .then((r) => r.json())
        .then(setViewData)
        .finally(() => setLoadingRec(false));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-select the latest episode once viewData is loaded in readOnly mode
  useEffect(() => {
    if (readOnly && viewData && selectedEpisode === undefined) {
      const episodes = viewData.episodes || [];
      if (episodes.length > 0) {
        setSelectedEpisode(episodes[episodes.length - 1]);
        setLocalStep(2);
      } else {
        setSelectedEpisode(null); // no episodes yet → admin can fill new EK
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewData, readOnly]);

  // selectedEpisode: undefined = loading, null = new EK mode, object = viewing episode
  const ekSaved   = readOnly && selectedEpisode !== null && selectedEpisode !== undefined;
  const canEditEK = readOnly && selectedEpisode === null && !!viewData;

  // When readOnly, use viewData instead of context; PP comes from selected episode or localPP
  const effectiveData = readOnly && viewData ? {
    t1: { ...DEFAULT_T1, ...(viewData.t1_data || {}) },
    t2: viewData.t2_data || {},
    pp: canEditEK
      ? localPP
      : selectedEpisode
        ? { ...DEFAULT_PP, ...(selectedEpisode.pp_data || {}) }
        : { ...DEFAULT_PP },
    name: viewData.name || '',
    vorname: viewData.vorname || '',
    patientenId: viewData.patienten_id || '',
  } : data;

  const { t1, t2, pp, name, vorname, patientenId } = effectiveData;

  // When viewing a saved record, use local step so context isn't touched
  const [localStep, setLocalStep] = useState(0);
  const [showNameError, setShowNameError] = useState(false);
  const step    = readOnly ? localStep : (startAtEK ? Math.max(data.step ?? 0, 2) : (data.step ?? 0));
  const setStep = readOnly ? setLocalStep : (n) => setData((prev) => ({ ...prev, step: n }));
  const tab     = ["t1","t2","pp"][step];

  const setName    = readOnly ? () => {} : (v) => setData((prev) => ({ ...prev, name: v }));
  const setVorname = readOnly ? () => {} : (v) => setData((prev) => ({ ...prev, vorname: v }));

  // Aliases — no-ops when readOnly; PP setters use localPP when admin can fill EK
  const noop = () => {};
  const st1 = readOnly ? noop : setT1;
  const st2 = readOnly ? noop : setT2;
  const spp = canEditEK
    ? (field, val) => setLocalPP((prev) => ({ ...prev, [field]: val }))
    : (readOnly ? noop : setPP);

  const toggleT1Arr = readOnly ? noop : (field, val) => {
    const arr = t1[field] || [];
    setT1(field, arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val]);
  };
  const togglePPArr = canEditEK
    ? (field, val) => setLocalPP((prev) => {
        const arr = prev[field] || [];
        return { ...prev, [field]: arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val] };
      })
    : (readOnly ? noop : (field, val) => {
        const arr = pp[field] || [];
        setPP(field, arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val]);
      });

  const setAkt = (stateKey, act, choice) => {
    if (stateKey === "t1") {
      if (!readOnly) setT1("aktivitaeten", { ...t1.aktivitaeten, [act]: choice });
    } else if (canEditEK) {
      setLocalPP((prev) => ({ ...prev, aktivitaeten: { ...prev.aktivitaeten, [act]: choice } }));
    } else if (!readOnly) {
      setPP("aktivitaeten", { ...pp.aktivitaeten, [act]: choice });
    }
  };

  const handleSaveVorerhebung = async () => {
    setSaving(true);
    setSaveMsg("");
    try {
      const isPublic = !!token;
      const url = isPublic
        ? `${API}/api/mos/fragebogen/vorerhebung`
        : `${API}/api/mos-fragebogen/vorerhebung`;
      const headers = isPublic
        ? { "Content-Type": "application/json" }
        : authHeaders();
      const body = isPublic
        ? JSON.stringify({ token, code, patientenId, name, vorname, t1, t2 })
        : JSON.stringify({ patientenId, name, vorname, t1, t2 });

      const res = await fetch(url, { method: "POST", headers, body });
      const record = await res.json();
      if (!res.ok) {
        console.error("Save Vorerhebung failed:", res.status, record);
        setSaveMsg(record?.error || "Fehler beim Speichern.");
        setSaving(false);
        return;
      }
      setSavedFragebogenId(record.id);
      clearMOSFragebogen();
      setSaving(false);
      setShowSuccess(true);
      window.scrollTo(0, 0);
    } catch (err) {
      console.error("Save Vorerhebung exception:", err);
      setSaveMsg("Fehler beim Speichern.");
      setSaving(false);
    }
  };

  const handleSaveErfolgskontrolle = async () => {
    const fid = (readOnly || startAtEK) ? (resolvedFragebogenId || viewData?.id) : (data.fragebogenId || savedFragebogenId);
    if (!fid) { setSaveMsg("Bitte zuerst die Vorerhebung speichern."); return; }
    setSaving(true);
    setSaveMsg("");
    try {
      const isPublic = !!token;
      const url = isPublic
        ? `${API}/api/mos/fragebogen/${fid}/episodes`
        : `${API}/api/mos-fragebogen/${fid}/episodes`;
      const headers = isPublic
        ? { "Content-Type": "application/json" }
        : authHeaders();
      const body = isPublic
        ? JSON.stringify({ token, code, pp })
        : JSON.stringify({ pp });

      const res = await fetch(url, { method: "POST", headers, body });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        setSaveMsg(err?.error || "Fehler beim Speichern.");
        return;
      }
      const savedEpisode = await res.json();
      setEKJustSaved(true);
      if (readOnly) {
        setViewData((prev) => ({
          ...prev,
          episodes: (prev.episodes || []).some((e) => e.id === savedEpisode.id)
            ? prev.episodes
            : [...(prev.episodes || []), savedEpisode],
        }));
        setSelectedEpisode(savedEpisode);
        setLocalPP({ ...DEFAULT_PP });
        setShowEKSuccess(true);
        window.scrollTo(0, 0);
      } else {
        clearMOSFragebogen();
        setShowEKSuccess(true);
      }
    } catch {
      setSaveMsg("Fehler beim Speichern.");
    } finally {
      setSaving(false);
    }
  };

  const showT1WundenFU = t1.wunden === "Ja";
  const showT1Q15     = t1.arztBesprochen === "Ja (weiter mit Frage 15)";
  const showT2Q29     = t2.techBesprochen === "Ja (weiter mit Frage 28)";
  const showPPTrage   = pp.trageHaeufigkeit !== "" && pp.trageHaeufigkeit !== "Nie";
  const showPPQ34     = pp.zieleErfuellt === "Nein (wenn nein, weiter mit Frage 34)";

  if (loadingRec) {
    return <p style={{ padding: "3rem", textAlign: "center", color: "#aaa" }}>Wird geladen…</p>;
  }

  return (
    <div className={styles.page}>
      {readOnly && (
        <div style={{ marginBottom: "1rem" }}>
          <button className={styles["back-btn"]} onClick={() => onDone ? onDone() : navigate(-1)}>
            Zurück
          </button>
        </div>
      )}

      {showEKSuccess ? (
        <div className={styles.successScreen}>
          <div className={styles.successIcon}>✓</div>
          <h2 className={styles.successTitle}>Erfolgskontrolle gespeichert!</h2>
          <p className={styles.successMsg}>
            {token
              ? "Vielen Dank! Ihre Antworten wurden erfolgreich gespeichert. Sie können diese Seite jetzt schließen. Dieser Link ist nun nicht mehr gültig – für die nächste Erfolgskontrolle erhalten Sie einen neuen Zugang von Ihrem Administrator."
              : "Die Erfolgskontrolle wurde erfolgreich erfasst. Weitere Erfolgskontrollen können jederzeit über die Patientenliste hinzugefügt werden."}
          </p>
          {!token && (
            <div className={styles["form-actions"]} style={{ justifyContent: "center" }}>
              <button type="button" className={styles["back-btn"]}
                onClick={() => onDone ? onDone() : navigate("/mos-patienten")}>
                Zurück zur Liste
              </button>
            </div>
          )}
        </div>
      ) : showSuccess ? (
        <div className={styles.successScreen}>
          <div className={styles.successIcon}>✓</div>
          <h2 className={styles.successTitle}>Vorerhebung gespeichert!</h2>
          <p className={styles.successMsg}>
            {token
              ? "Vielen Dank! Ihre Antworten wurden erfolgreich gespeichert. Sie können diese Seite jetzt schließen."
              : "Die Vorerhebung wurde erfolgreich erfasst. Die Erfolgskontrolle kann zu einem späteren Zeitpunkt ausgefüllt werden (z. B. nach einigen Monaten)."}
          </p>
          {!token && (
            <div className={styles["form-actions"]} style={{ justifyContent: "center" }}>
              <button type="button" className={styles["back-btn"]}
                onClick={() => onDone ? onDone() : navigate("/start")}>
                Zurück zur Liste
              </button>
              <button type="button" className={styles["submit-btn"]}
                onClick={() => { setShowSuccess(false); setStep(2); window.scrollTo(0, 0); }}>
                Erfolgskontrolle jetzt ausfüllen
              </button>
            </div>
          )}
        </div>
      ) : (
        <>
      <h1>MOS Fragebogen</h1>
      <p className={styles.subtitle}>Vorerhebung &amp; Erfolgskontrolle</p>

      {/* Episode selector — shown only in admin readOnly mode */}
      {readOnly && viewData && (
        <div className={styles.episodeBar}>
          <span className={styles.episodeBarLabel}>Erfolgskontrolle:</span>
          {(viewData.episodes || []).map((ep) => (
            <button
              key={ep.id}
              className={`${styles.episodeChip}${selectedEpisode?.id === ep.id ? " " + styles.episodeChipActive : ""}`}
              onClick={() => { setSelectedEpisode(ep); setLocalStep(2); }}
            >
              {ep.episode_number}: ({fmtDate(ep.completed_at)})
            </button>
          ))}
          <button
            className={`${styles.episodeChip} ${styles.episodeChipNew}${selectedEpisode === null ? " " + styles.episodeChipActive : ""}`}
            onClick={() => { setSelectedEpisode(null); setLocalPP({ ...DEFAULT_PP }); setLocalStep(2); }}
          >
            + Neue EK
          </button>
        </div>
      )}

      {/* Step indicator */}
      <div className={styles.stepBar}>
        {["Vorerhebung Teil 1","Vorerhebung Teil 2","Erfolgskontrolle"].map((label, i) => (
          <div key={i} className={`${styles.stepItem}${step === i ? " " + styles.stepItemActive : step > i ? " " + styles.stepItemDone : ""}`}>
            <span className={styles.stepDot}>{step > i ? "✓" : i + 1}</span>
            <span className={styles.stepLabel}>{label}</span>
          </div>
        ))}
      </div>

      {/* ══════════════════════════════════════════
          VORERHEBUNG TEIL 1
      ══════════════════════════════════════════ */}
      {tab === "t1" && (
        <form noValidate onSubmit={(e) => e.preventDefault()} className={readOnly ? styles.readonlyWrapper : ""}>
          <p className={styles.partLabel}>I. Vorerhebung (Pre-Part) – Teil 1</p>

          {/* A. Persönliche Angaben */}
          <section className={styles.card}>
            <h2 className={styles.sectionTitle}>A. Persönliche Angaben</h2>
            <div className={styles.question}>
              <label className={styles.questionLabel} htmlFor="t1_pid">
                <span className={styles.qNum}>1.</span> Patienten-ID
              </label>
              <input id="t1_pid" type="text" className={`${styles.textInput} ${styles.readonlyInput}`}
                value={patientenId} readOnly />
            </div>
            <div className={styles.question}>
              <label className={styles.questionLabel} htmlFor="t1_name">
                Name <span className={styles.requiredMark}>*</span>
              </label>
              <input id="t1_name" type="text"
                className={`${styles.textInput}${nameLocked ? " " + styles.readonlyInput : ""}${showNameError && !name.trim() ? " " + styles.inputError : ""}`}
                value={name} readOnly={nameLocked}
                onChange={(e) => { setName(e.target.value); if (e.target.value.trim()) setShowNameError(false); }}
                placeholder="Nachname" />
              {showNameError && !name.trim() && <p className={styles.errorHint}>Bitte Wert eingeben</p>}
            </div>
            <div className={styles.question}>
              <label className={styles.questionLabel} htmlFor="t1_vorname">
                Vorname <span className={styles.requiredMark}>*</span>
              </label>
              <input id="t1_vorname" type="text"
                className={`${styles.textInput}${vornameLocked ? " " + styles.readonlyInput : ""}${showNameError && !vorname.trim() ? " " + styles.inputError : ""}`}
                value={vorname} readOnly={vornameLocked}
                onChange={(e) => { setVorname(e.target.value); if (e.target.value.trim()) setShowNameError(false); }}
                placeholder="Vorname" />
              {showNameError && !vorname.trim() && <p className={styles.errorHint}>Bitte Wert eingeben</p>}
            </div>
            <Radios n="2" label="Alter" name="t1_alter" opts={ALTER} value={t1.alter} onChange={(v) => st1("alter", v)} />
            <Radios n="3" label="Geschlecht" name="t1_gesch" opts={["Männlich","Weiblich","Divers"]} value={t1.geschlecht} onChange={(v) => st1("geschlecht", v)} />
            <div className={styles.question}>
              <p className={styles.subSectionHeader}>Beruf</p>
              <p className={styles.subSectionNote}>Bitte geben Sie Ihre derzeitige berufliche Tätigkeit an:</p>
            </div>
            <Radios n="4" label="Welche der folgenden Berufsfelder entspricht Ihrer Tätigkeit am ehesten?" name="t1_bf" opts={BERUFSFELDER} value={t1.berufsfeld} onChange={(v) => st1("berufsfeld", v)} />
            <Radios n="5" label="Welcher der folgenden beruflichen Status trifft derzeit auf Sie zu?" name="t1_bs" opts={BERUFSSTATUS} value={t1.berufsstatus} onChange={(v) => st1("berufsstatus", v)} />
          </section>

          {/* B. Aktuelle Situation */}
          <section className={styles.card}>
            <h2 className={styles.sectionTitle}>B. Aktuelle Situation</h2>

            {/* Q6 – checkboxes */}
            <div className={styles.question}>
              <p className={styles.questionLabel}>
                <span className={styles.qNum}>6.</span> Welche der folgenden Erkrankungen haben Sie?{" "}
                <span className={styles.hint}>(Mehrfachantwort möglich)</span>
              </p>
              <div className={styles.optionList}>
                {["Diabetes","Rheumatoide Arthritis",
                  "Fußfehlstellung (z. B. Plattfuß, Krallenzehen, Hallux valgus)",
                  "Muskelerkrankung"].map((opt) => (
                  <label key={opt} className={styles.option}>
                    <input type="checkbox" checked={t1.erkrankungen.includes(opt)}
                      onChange={() => toggleT1Arr("erkrankungen", opt)} />
                    <span>{opt}</span>
                  </label>
                ))}
                <label className={styles.option}>
                  <input type="checkbox" checked={t1.erkrankungen.includes("Sonstiges")}
                    onChange={() => toggleT1Arr("erkrankungen", "Sonstiges")} />
                  <span>Sonstiges, bitte angeben:</span>
                </label>
                {t1.erkrankungen.includes("Sonstiges") && (
                  <input type="text" className={styles.textInput}
                    value={t1.erkrankungenSonstiges ?? ""}
                    onChange={(e) => st1("erkrankungenSonstiges", e.target.value)}
                    placeholder="Bitte angeben" maxLength={200}
                    style={{ marginLeft: 20, marginTop: 4 }} />
                )}
              </div>
            </div>

            <Radios n="7" label="Wie weit können Sie aktuell gehen?" hint="(ICF: d450)"
              name="t1_gehs" opts={GEHSTRECKE_NOW} value={t1.gehstrecke} onChange={(v) => st1("gehstrecke", v)} />

            <Radios n="8" label="Hatten Sie Wunden oder Geschwüre an Füßen oder Knöcheln?" hint="(ICF: s810, b810)"
              name="t1_wunden" opts={["Ja","Nein (bei Nein, weiter mit Frage 12)"]}
              value={t1.wunden} onChange={(v) => st1("wunden", v)} />
            {t1.wunden === "Nein (bei Nein, weiter mit Frage 12)" && <SkipNote text="Weiter mit Frage 12" />}

            <div className={!showT1WundenFU ? styles.dimmedBlock : ""}>
              <div className={styles.question}>
                <p className={styles.questionLabel}>
                  <span className={styles.qNum}>9.</span> Bitte markieren Sie die Stelle der Wunden/Geschwüre auf der Abbildung.
                </p>
                <FootDiagram
                  value={t1.wundenLokalisierung}
                  onChange={(v) => st1("wundenLokalisierung", v)}
                />
              </div>
            </div>
          </section>

          {/* C. Erwartungen an die orthopädischen Schuhe */}
          <section className={styles.card}>
            <h2 className={styles.sectionTitle}>C. Erwartungen an die orthopädischen Schuhe</h2>

            <div className={!showT1WundenFU ? styles.dimmedBlock : ""}>
              <Radios n="10" label="Erwarten Sie durch die Schuhe weniger Wunden an Ihren Füßen?" hint="(ICF s810, b810)"
                name="t1_wundenErw" opts={["Ja","Nein","Ich bin mir unsicher"]}
                value={t1.wundenErwartung} onChange={(v) => st1("wundenErwartung", v)} />
              <Radios n="11" label="Erwarten Sie, dass Sie durch die neue Schuhversorgung weniger Krankenhausaufenthaltstage wegen einer Wunde an den Füßen haben werden?" hint="(ICF: d76, s810, b810)"
                name="t1_krankErw" opts={["Ja","Nein","Ich bin mir unsicher"]}
                value={t1.krankenhausErwartung} onChange={(v) => st1("krankenhausErwartung", v)} />
            </div>

            <Matrix n="12"
              label="Erwarten Sie, dass Sie mit Ihren orthopädischen Schuhen die folgenden Aktivitäten weniger oder mehr ausüben können?"
              hint="(ICF: d450, d455, d460, d640, d920)"
              note='Wenn Sie keine Veränderung erwarten, wählen Sie „keine Veränderung" aus.'
              prefix="t1_akt" state={t1.aktivitaeten}
              onChange={(act, c) => setAkt("t1", act, c)} />
          </section>

          {/* D. Gespräch mit dem Arzt */}
          <section className={styles.card}>
            <h2 className={styles.sectionTitle}>D. Gespräch mit dem Arzt</h2>
            <Scale n="13" label="Wie gut hat Ihnen der Arzt zugehört?" hint="(ICF: e450, d710, e355)"
              left="sehr schlecht" right="sehr gut"
              name="t1_arztZ" value={t1.arztZugehoert} onChange={(v) => st1("arztZugehoert", v)} />
            <Radios n="14" label="Hat der Arzt mit Ihnen besprochen, was Sie von den orthopädischen Schuhen erwarten können?" hint="(ICF: e450, d710, e355)"
              name="t1_arztB"
              opts={["Ja (weiter mit Frage 15)","Nein (weiter mit Frage 16)","Ich kann mich nicht erinnern (weiter mit Frage 16)"]}
              value={t1.arztBesprochen} onChange={(v) => st1("arztBesprochen", v)} />
            <div className={!showT1Q15 ? styles.dimmedBlock : ""}>
              <Radios n="15" label="Haben Sie nach dem Gespräch mit dem Arzt Ihre Erwartungen angepasst?" hint="(ICF: d7400, d710)"
                name="t1_erwAng"
                opts={["Ja, ich erwarte jetzt mehr","Ja, ich erwarte jetzt weniger",
                       "Nein, meine Erwartungen sind gleichgeblieben","Ich hatte keine Erwartungen"]}
                value={t1.erwartungenAngepasst} onChange={(v) => st1("erwartungenAngepasst", v)} />
            </div>
          </section>

          {/* E. Erwartungen an das Aussehen */}
          <section className={styles.card}>
            <h2 className={styles.sectionTitle}>E. Erwartungen an das Aussehen der orthopädischen Schuhe</h2>
            <Scale n="16" label="Wie hässlich oder attraktiv erwarten Sie Ihre Schuhe?" hint="(ICF: d920, b1800)"
              left="sehr hässlich" right="sehr attraktiv"
              name="t1_ausseh" value={t1.schuheAussehen} onChange={(v) => st1("schuheAussehen", v)} />
            <Radios n="17" label="Was denken Sie, wie andere das Aussehen Ihrer orthopädischen Schuhe beurteilen werden?" hint="(ICF e460)"
              name="t1_andM"
              opts={["sehr hässlich","hässlich","neutral","schön","sehr schön",
                     "Ich weiß nicht, was andere über das Aussehen meiner Schuhe denken"]}
              value={t1.andereMeinung} onChange={(v) => st1("andereMeinung", v)} />
          </section>

          {/* F. Nutzung */}
          <section className={styles.card}>
            <h2 className={styles.sectionTitle}>F. Nutzung der orthopädischen Schuhe</h2>
            <Scale n="18" label="Wie gut erwarten Sie, dass Ihre Schuhe passen?" hint="(ICF: d540)"
              left="sehr schlecht" right="sehr gut"
              name="t1_pass" value={t1.schuhePassen} onChange={(v) => st1("schuhePassen", v)} />
            <Radios n="19" label="Wie weit erwarten Sie mit den Schuhen gehen zu können?" hint="(ICF: d450)"
              name="t1_gehsExp" opts={GEHSTRECKE_EXP}
              value={t1.schuheGehstrecke} onChange={(v) => st1("schuheGehstrecke", v)} />
            <Radios n="20" label="Ist das mehr oder weniger als Sie aktuell gehen können?" hint="(ICF d450)"
              name="t1_vsAkt" opts={["Weniger","Gleich viel","Mehr"]}
              value={t1.schuheVsAktuell} onChange={(v) => st1("schuheVsAktuell", v)} />
          </section>

          {/* G. Prioritäten */}
          <section className={styles.card}>
            <h2 className={styles.sectionTitle}>G. Bewertung der Prioritäten</h2>
            <Radios n="21" label="Was ist Ihnen wichtiger: Dass die Schuhe gut aussehen oder Ihre Fußprobleme lösen?" hint="(ICF: b1800, s810, b810)"
              name="t1_prio"
              opts={["Das Aussehen ist wichtiger","Beides ist gleich wichtig","Die Lösung der Fußprobleme ist wichtiger"]}
              value={t1.prioritaet} onChange={(v) => st1("prioritaet", v)} />
            <Scale n="22" label="Erwarten Sie, dass die Vorteile die Nachteile überwiegen?"
              left="auf keinen Fall" right="auf jeden Fall"
              name="t1_vortUeb" value={t1.vorteileUeberwiegen} onChange={(v) => st1("vorteileUeberwiegen", v)} />
          </section>

          {/* H. Abschluss */}
          <section className={styles.card}>
            <h2 className={styles.sectionTitle}>H. Abschluss</h2>
            <Txt n="23" label="Haben Sie weitere Anmerkungen:" id="t1_anm"
              value={t1.anmerkungen} onChange={(v) => st1("anmerkungen", v)} rows={3} maxLength={200} />
            <Scale n="24" label="Hatten Sie Schwierigkeit bei der Beantwortung der Fragen?"
              left="Keine" right="sehr große"
              name="t1_schwier" value={t1.schwierigkeit} onChange={(v) => st1("schwierigkeit", v)} />
            <div className={styles.question}>
              <label className={styles.questionLabel} htmlFor="t1_zeit">
                <span className={styles.qNum}>25.</span> Wieviel Zeit haben Sie für die Beantwortung der Fragen benötigt?
              </label>
              <div className={styles.inlineInputRow}>
                <input id="t1_zeit" type="number" min="0" max="1440" className={styles.textInput}
                  style={{ maxWidth: 120 }} value={t1.zeitMinuten ?? ""} placeholder="0"
                  onChange={(e) => {
                    const v = e.target.value === "" ? "" : Math.min(1440, Math.max(0, Number(e.target.value)));
                    st1("zeitMinuten", String(v));
                  }} />
                <span className={styles.inputUnit}>Minuten</span>
              </div>
            </div>
          </section>

          {showNameError && (!name.trim() || !vorname.trim()) && (
            <p className={styles.errorHintBtn}>Bitte alle Pflichtfelder ausfüllen.</p>
          )}
          <div className={styles["form-actions"]}>
            {!token && (
              <button type="button" className={styles["back-btn"]}
                onClick={() => onDone ? onDone() : navigate("/mos-patienten")}>
                Zurück
              </button>
            )}
            <button type="button" className={styles["submit-btn"]} onClick={() => {
              if (!name.trim() || !vorname.trim()) {
                setShowNameError(true);
                return;
              }
              setShowNameError(false);
              setStep(1); window.scrollTo(0, 0);
            }}>Weiter</button>
          </div>
        </form>
      )}

      {/* ══════════════════════════════════════════
          VORERHEBUNG TEIL 2
      ══════════════════════════════════════════ */}
      {tab === "t2" && (
        <form noValidate onSubmit={(e) => e.preventDefault()} className={readOnly ? styles.readonlyWrapper : ""}>
          <p className={styles.partLabel}>I. Vorerhebung (Pre-Part) – Teil 2</p>

          <section className={styles.card}>
            <h2 className={styles.sectionTitle}>I. Gespräch mit dem Orthopädietechniker</h2>
            <Scale n="26" label="Wie gut hat Ihnen der Orthopädie (Schuh) Techniker zugehört?" hint="(ICF: e450, d710, e355)"
              left="sehr schlecht" right="sehr gut"
              name="t2_techZ" value={t2.techZugehoert} onChange={(v) => st2("techZugehoert", v)} />
            <Radios n="27" label="Hat der Orthopädie (Schuh) Techniker mit Ihnen besprochen, was Sie von den orthopädischen Schuhen erwarten können?" hint="(ICF: e450, d710, e355)"
              name="t2_techB"
              opts={["Ja (weiter mit Frage 28)","Nein (weiter mit Frage 29)","Ich kann mich nicht erinnern (weiter mit Frage 29)"]}
              value={t2.techBesprochen} onChange={(v) => st2("techBesprochen", v)} />
            <div className={!showT2Q29 ? styles.dimmedBlock : ""}>
              <Radios n="28" label="Haben Sie nach dem Gespräch Ihre Erwartungen angepasst?" hint="(ICF: d7400, d710)"
                name="t2_techErwAng"
                opts={["Ja, ich erwarte jetzt mehr","Ja, ich erwarte jetzt weniger",
                       "Nein, meine Erwartungen sind gleichgeblieben","Ich hatte keine Erwartungen"]}
                value={t2.techErwartungenAngepasst} onChange={(v) => st2("techErwartungenAngepasst", v)} />
            </div>
            <Radios n="29" label="Konnten Sie bei der Gestaltung/Aussehen der Schuhe mitentscheiden?" hint="(ICF: d740)"
              name="t2_gestalt" opts={["Ja","Nein"]}
              value={t2.gestaltungMitentschieden} onChange={(v) => st2("gestaltungMitentschieden", v)} />
          </section>

          <div className={styles["form-actions"]}>
            <button type="button" className={styles["back-btn"]} onClick={() => { setStep(0); window.scrollTo(0,0); }}>Zurück</button>
            <button type="button" className={styles["save-btn"]} onClick={handleSaveVorerhebung}
              disabled={saving || readOnly}>
              {readOnly ? "Vorerhebung gespeichert ✓" : saving ? "Wird gespeichert…" : "Vorerhebung speichern"}
            </button>
            {readOnly && (
              <button type="button" className={styles["submit-btn"]} onClick={() => { setStep(2); window.scrollTo(0,0); }}>Weiter</button>
            )}
          </div>
          {saveMsg && <p className={styles.saveMsg}>{saveMsg}</p>}
        </form>
      )}

      {/* ══════════════════════════════════════════
          ERFOLGSKONTROLLE (POST-PART)
      ══════════════════════════════════════════ */}
      {tab === "pp" && (token && ekJustSaved ? (
        <div className={styles.successScreen}>
          <div className={styles.successIcon}>✓</div>
          <h2 className={styles.successTitle}>Vielen Dank!</h2>
          <p className={styles.successMsg}>
            Ihr Fragebogen wurde vollständig ausgefüllt. Sie können diese Seite jetzt schließen.
          </p>
        </div>
      ) :
        <form noValidate onSubmit={(e) => e.preventDefault()} className={ekSaved ? styles.readonlyWrapper : ""}>
          <p className={styles.partLabel}>II. Erfolgskontrolle (Post-Part)</p>

          {/* A. Persönliche Angaben */}
          <section className={styles.card}>
            <h2 className={styles.sectionTitle}>A. Persönliche Angaben</h2>
            <div className={styles.question}>
              <label className={styles.questionLabel} htmlFor="pp_pid">
                <span className={styles.qNum}>1.</span> Patienten-ID
              </label>
              <input id="pp_pid" type="text" className={`${styles.textInput} ${styles.readonlyInput}`}
                value={patientenId} readOnly />
            </div>
            <Radios n="2" label="Alter" name="pp_alter" opts={ALTER} value={pp.alter} onChange={(v) => spp("alter", v)} />
            <Radios n="3" label="Geschlecht" name="pp_gesch" opts={["Männlich","Weiblich","Divers"]} value={pp.geschlecht} onChange={(v) => spp("geschlecht", v)} />
            <div className={styles.question}>
              <p className={styles.subSectionHeader}>Beruf</p>
              <p className={styles.subSectionNote}>Bitte geben Sie Ihre derzeitige berufliche Tätigkeit an:</p>
            </div>
            <Radios n="4" label="Welche der folgenden Berufsfelder entspricht Ihrer Tätigkeit am ehesten?" name="pp_bf" opts={BERUFSFELDER} value={pp.berufsfeld} onChange={(v) => spp("berufsfeld", v)} />
            <Radios n="5" label="Welcher der folgenden beruflichen Status trifft derzeit auf Sie zu?" name="pp_bs" opts={BERUFSSTATUS} value={pp.berufsstatus} onChange={(v) => spp("berufsstatus", v)} />
          </section>

          {/* B. Aktuelle Situation */}
          <section className={styles.card}>
            <h2 className={styles.sectionTitle}>B. Aktuelle Situation</h2>
            <Radios n="6" label="Wie weit können Sie aktuell gehen?" hint="(ICF: d450)"
              name="pp_gehs" opts={GEHSTRECKE_NOW} value={pp.gehstrecke} onChange={(v) => spp("gehstrecke", v)} />
            <Radios n="7" label="Im Vergleich zur Zeit vor Erhalt Ihrer orthopädischen Schuhe, hat sich Ihre Gehfähigkeit…" hint="(ICF: d450)"
              name="pp_gehf"
              opts={["… durch die Schuhe verbessert","… verbessert, aber nicht wegen der Schuhe",
                     "… nicht verändert","… verschlechtert, aber nicht wegen der Schuhe",
                     "… durch die Schuhe verschlechtert"]}
              value={pp.gehfaehigkeit} onChange={(v) => spp("gehfaehigkeit", v)} />
            <Radios n="8" label="Im Vergleich zur Zeit vor Erhalt Ihrer orthopädischen Schuhe, hat sich Ihr allgemeiner Gesundheitszustand (nicht nur Ihre Füße)" hint="(ICF: b130s)"
              name="pp_gesund" opts={["… verbessert","… nicht verändert","… verschlechtert"]}
              value={pp.gesundheitszustand} onChange={(v) => spp("gesundheitszustand", v)} />

            {/* Q9 – multiple checkbox */}
            <div className={styles.question}>
              <p className={styles.questionLabel}>
                <span className={styles.qNum}>9.</span> Haben Ihre orthopädischen Schuhe zu Veränderungen an den Wunden an Ihren Füßen und/oder Knöcheln geführt?{" "}
                <span className={styles.hint}>(Mehrere Antworten möglich) (ICF s810, b810)</span>
              </p>
              <div className={styles.optionList}>
                {["mehr Wunden","größere Wunden","keine Veränderung",
                  "weniger Wunden","kleinere Wunden","trifft nicht auf mich zu"].map((opt) => (
                  <label key={opt} className={styles.option}>
                    <input type="checkbox" checked={pp.wundenVer.includes(opt)}
                      onChange={() => togglePPArr("wundenVer", opt)} />
                    <span>{opt}</span>
                  </label>
                ))}
              </div>
            </div>

            <Radios n="10" label="Verbrachten Sie seit Erhalt Ihrer orthopädischen Schuhe aufgrund von Fußproblemen weniger Tage im Krankenhaus?" hint="(ICF: d760, s810, b810)"
              name="pp_kh"
              opts={["Ja, ich hatte weniger Krankenhaus Aufenthaltstage",
                     "Nein, ich hatte genauso viele Krankenhaus Aufenthaltstage",
                     "Nein, ich hatte mehr Krankenhaus Aufenthaltstage"]}
              value={pp.krankenhaus} onChange={(v) => spp("krankenhaus", v)} />
          </section>

          {/* C. Aussehen */}
          <section className={styles.card}>
            <h2 className={styles.sectionTitle}>C. Ihre Meinung zum Aussehen Ihrer orthopädischen Schuhe</h2>
            <Scale n="11" label="Wie hässlich oder schön finden Sie Ihre orthopädischen Schuhe?" hint="(ICF: d920, b1800)"
              left="Sehr hässlich" right="sehr schön"
              name="pp_ausseh" value={pp.aussehen} onChange={(v) => spp("aussehen", v)} />
            <Radios n="12" label="Was denken andere über das Aussehen Ihrer orthopädischen Schuhe?" hint="(ICF: e460)"
              name="pp_andM" opts={["Sehr hässlich","Hässlich","Neutral","Attraktiv","Sehr attraktiv","Ich weiß es nicht"]}
              value={pp.andereMeinung} onChange={(v) => spp("andereMeinung", v)} />
          </section>

          {/* D. Gebrauch */}
          <section className={styles.card}>
            <h2 className={styles.sectionTitle}>D. Gebrauch Ihrer orthopädischen Schuhe</h2>
            <Scale n="13" label="Wie schlecht oder gut passen Ihre orthopädischen Schuhe?" hint="(ICF: e1151)"
              left="Sehr schlecht" right="sehr gut"
              name="pp_pass" value={pp.passen} onChange={(v) => spp("passen", v)} />
            <Scale n="14" label="Passen Ihre orthopädischen Schuhe schlechter oder besser als erwartet?" hint="(ICF: e1151, d540)"
              left="Viel schlechter" right="viel besser"
              name="pp_passExp" value={pp.passenVsErwartet} onChange={(v) => spp("passenVsErwartet", v)} />
            <Scale n="15" label="Wie schlecht oder gut können Sie mit Ihren orthopädischen Schuhen gehen?" hint="(ICF: d450)"
              left="sehr schlecht" right="sehr gut"
              name="pp_geh" value={pp.gehen} onChange={(v) => spp("gehen", v)} />
            <Scale n="16" label="Können Sie mit Ihren orthopädischen Schuhen schlechter oder besser gehen als erwartet?" hint="(ICF: d450)"
              left="viel schlechter" right="viel besser"
              name="pp_gehExp" value={pp.gehenVsErwartet} onChange={(v) => spp("gehenVsErwartet", v)} />
            <Scale n="17" label="Wie empfinden Sie das Gewicht Ihrer orthopädischen Schuhe?" hint="(ICF: e1151)"
              left="zu leicht" right="zu schwer"
              name="pp_gew" value={pp.gewicht} onChange={(v) => spp("gewicht", v)} />
            <Scale n="18" label="Sind Ihre orthopädischen Schuhe leichter oder schwerer als erwartet?" hint="(ICF: e1151)"
              left="viel leichter" right="viel schwerer"
              name="pp_gewExp" value={pp.gewichtVsErwartet} onChange={(v) => spp("gewichtVsErwartet", v)} />
            <Scale n="19" label="Wie schwierig ist es, Ihre orthopädischen Schuhe an- und auszuziehen?" hint="(ICF: d5402, d5403)"
              left="sehr schwierig" right="sehr einfach"
              name="pp_anz" value={pp.anziehen} onChange={(v) => spp("anziehen", v)} />
            <Matrix n="20"
              label="Konnten Sie seit Erhalt Ihrer orthopädischen Schuhe Ihre gewohnten Aktivitäten an mehr oder weniger Tagen uneingeschränkt ausüben?"
              hint="(ICF: d450, d455, d460, d640, d920)"
              prefix="pp_akt" state={pp.aktivitaeten}
              onChange={(act, c) => setAkt("pp", act, c)} />
          </section>

          {/* E. Wunden aktuell */}
          <section className={styles.card}>
            <h2 className={styles.sectionTitle}>E. Aktuelle Situation Wunden / Geschwüre</h2>
            <Radios n="21" label="Haben Sie Wunden oder Geschwüre an Füßen oder Knöcheln?" hint="(ICF s810, b810)"
              name="pp_wundAkt" opts={["Ja","Nein (Bei Nein, weiter mit Frage 23)"]}
              value={pp.wundenAktuell} onChange={(v) => spp("wundenAktuell", v)} />
            {pp.wundenAktuell === "Nein (Bei Nein, weiter mit Frage 23)" && <SkipNote text="Weiter mit Frage 23" />}
            <div className={pp.wundenAktuell !== "Ja" ? styles.dimmedBlock : ""}>
              <div className={styles.question}>
                <p className={styles.questionLabel}>
                  <span className={styles.qNum}>22.</span> Bitte markieren Sie die Stelle der Wunden/Geschwüre auf der Abbildung.
                </p>
                <FootDiagram
                  value={pp.wundenLokalisierung}
                  onChange={(v) => spp("wundenLokalisierung", v)}
                />
              </div>
            </div>
          </section>

          {/* F. Tragehäufigkeit */}
          <section className={styles.card}>
            <h2 className={styles.sectionTitle}>F. Tragehäufigkeit</h2>
            <Radios n="23" label="Wie oft tragen Sie Ihre orthopädischen Schuhe?" hint="(ICF: d450, d850)"
              name="pp_trage"
              opts={["6–7 Tage pro Woche","4–5 Tage pro Woche","2–3 Tage pro Woche",
                     "1 Tag pro Woche","Nie"]}
              value={pp.trageHaeufigkeit} onChange={(v) => spp("trageHaeufigkeit", v)} />
            {pp.trageHaeufigkeit === "Nie" && <SkipNote text='Weiter mit Frage 26' />}
            <div className={!showPPTrage ? styles.dimmedBlock : ""}>
              <Radios n="24" label="Wenn Sie Ihre orthopädischen Schuhe tragen: Wie viele Stunden pro Tag?"
                name="pp_std"
                opts={["Mehr als 12 Stunden","8–12 Stunden","4–8 Stunden","1–4 Stunden","Weniger als 1 Stunde"]}
                value={pp.stunden} onChange={(v) => spp("stunden", v)} />
              <Radios n="25" label="Tragen Sie Ihre orthopädischen Schuhe so oft wie erwartet?"
                name="pp_trageErw" opts={["Ja","Nein","Ich weiß es nicht"]}
                value={pp.tragenWieErwartet} onChange={(v) => spp("tragenWieErwartet", v)} />
            </div>
            <Scale n="26" label="Wie zufrieden sind Sie mit der Häufigkeit und Dauer der Nutzung Ihrer orthopädischen Schuhe?"
              left="sehr unzufrieden" right="sehr zufrieden"
              name="pp_nutzZuf" value={pp.nutzungZufrieden} onChange={(v) => spp("nutzungZufrieden", v)} />
          </section>

          {/* G. Kommunikation und Zielerreichung */}
          <section className={styles.card}>
            <h2 className={styles.sectionTitle}>G. Kommunikation und Zielerreichung</h2>
            <Scale n="27" label="Wie gut hat Ihnen der Arzt bei der Nachkontrolle zugehört?" hint="(ICF: e450, d710, e355)"
              left="sehr schlecht" right="sehr gut"
              name="pp_arztNK" value={pp.arztNachkontrolle} onChange={(v) => spp("arztNachkontrolle", v)} />
            <Scale n="28" label="Wie gut hat Ihnen der Orthopädie (Schuh) Techniker bei der Übergabe/Nachkontrolle zugehört?" hint="(ICF: e450, d710, e355)"
              left="sehr schlecht" right="sehr gut"
              name="pp_techNK" value={pp.techNachkontrolle} onChange={(v) => spp("techNachkontrolle", v)} />
            <Radios n="29" label="Was ist Ihnen wichtiger: Dass die Schuhe gut aussehen oder Ihre Fußprobleme lösen?" hint="(ICF: b1800, s810, b810)"
              name="pp_prio"
              opts={["Das Aussehen ist wichtiger","Beides ist gleich wichtig","Die Lösung der Fußprobleme ist wichtiger"]}
              value={pp.prioritaet} onChange={(v) => spp("prioritaet", v)} />
            <Txt n="30" label="Was sind die Vorteile Ihrer orthopädischen Schuhe?" id="pp_vort"
              value={pp.vorteile} onChange={(v) => spp("vorteile", v)} rows={3} maxLength={200} />
            <Txt n="31" label="Was sind die Nachteile Ihrer orthopädischen Schuhe?" id="pp_nacht"
              value={pp.nachteile} onChange={(v) => spp("nachteile", v)} rows={3} maxLength={200} />
            <Scale n="32" label="Überwiegen die Vorteile die Nachteile?"
              left="auf keinen Fall" right="auf jeden Fall"
              name="pp_vortUeb" value={pp.vorteileUeberwiegen} onChange={(v) => spp("vorteileUeberwiegen", v)} />
            <Radios n="33" label="Haben Ihre orthopädischen Schuhe Ihre Ziele erfüllt?" hint="(ICF: d850, d920)"
              name="pp_ziele"
              opts={["Ja (wenn ja, weiter mit Frage 35)","Nein (wenn nein, weiter mit Frage 34)","Ich weiß es nicht (weiter mit Frage 35)"]}
              value={pp.zieleErfuellt} onChange={(v) => spp("zieleErfuellt", v)} />
            <div className={!showPPQ34 ? styles.dimmedBlock : ""}>
              <Txt n="34" label="Was ist der Grund, dass Ihre Ziele nicht erreicht wurden?" id="pp_zielGr"
                value={pp.zielGrund} onChange={(v) => spp("zielGrund", v)} rows={3} maxLength={200} />
            </div>
            <Txt n="35" label="Wie beurteilen Sie die Gebrauchstauglichkeit Ihrer orthopädischen Schuhe?" id="pp_gbt"
              value={pp.gebrauchstauglichkeit} onChange={(v) => spp("gebrauchstauglichkeit", v)} rows={3} maxLength={200} />
            <Txt n="36" label="Gibt es weitere Eigenschaften/Funktionen, die die Gebrauchstauglichkeit Ihrer Schuhe beeinflussen?" id="pp_gbtW"
              value={pp.weitereEigenschaften} onChange={(v) => spp("weitereEigenschaften", v)} rows={3} maxLength={200} />
            <Txt n="37" label="Haben Sie weitere Anmerkungen?" id="pp_anm"
              value={pp.anmerkungen} onChange={(v) => spp("anmerkungen", v)} rows={3} maxLength={200} />
            <Scale n="38" label="Hatten Sie Schwierigkeit bei der Beantwortung der Fragen?"
              left="Keine" right="sehr große"
              name="pp_schwier" value={pp.schwierigkeit} onChange={(v) => spp("schwierigkeit", v)} />
            <div className={styles.question}>
              <label className={styles.questionLabel} htmlFor="pp_zeit">
                <span className={styles.qNum}>39.</span> Wie lange haben Sie für das Ausfüllen des Fragebogens gebraucht?
              </label>
              <div className={styles.inlineInputRow}>
                <input id="pp_zeit" type="number" min="0" max="1440" className={styles.textInput}
                  style={{ maxWidth: 120 }} value={pp.zeitMinuten ?? ""} placeholder="0"
                  onChange={(e) => {
                    const v = e.target.value === "" ? "" : Math.min(1440, Math.max(0, Number(e.target.value)));
                    spp("zeitMinuten", String(v));
                  }} />
                <span className={styles.inputUnit}>Minuten</span>
              </div>
            </div>
          </section>

          <div className={styles["form-actions"]}>
            {!startAtEK && (
              <button type="button" className={styles["back-btn"]} onClick={() => { setStep(1); window.scrollTo(0,0); }}>Zurück</button>
            )}
            <button type="button" className={styles["save-btn"]} onClick={handleSaveErfolgskontrolle}
              disabled={saving || ekJustSaved || ekSaved}>
              {(ekJustSaved || ekSaved)
                ? "Erfolgskontrolle gespeichert ✓"
                : saving ? "Wird gespeichert…" : "Erfolgskontrolle speichern"}
            </button>
            {onDone && (ekJustSaved || ekSaved) && (
              <button type="button" className={styles["submit-btn"]} onClick={onDone}>
                Zur Patientenliste
              </button>
            )}
          </div>
          {saveMsg && <p className={styles.saveMsg}>{saveMsg}</p>}
        </form>
      )}
      </>
    )}
    </div>
  );
}
